import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatPrice } from '../propertyUtils';
import { API_BASE } from '../config';
import { Alert, Badge, Button } from './ComponentLibrary';
import './BookingCalendar.css';

/**
 * Request-to-book widget for short-let/hotel listings: guest picks dates,
 * sees a live conflict/price check against GET /properties/:id/availability
 * (real per-date-range check, not just the static window), and submits a
 * request. Never charges anything itself - payment only happens after the
 * seller approves, see PUT /bookings/:id/approve.
 */
export default function BookingCalendar({ propertyId, isOwner }) {
  const { user, authedFetch } = useAuth();
  const { showToast } = useToast();
  const currencyCtx = useCurrency();
  const navigate = useNavigate();

  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [defaults, setDefaults] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/properties/${propertyId}/availability`)
      .then((res) => res.json())
      .then((data) => setDefaults(data))
      .catch(() => {});
  }, [propertyId]);

  useEffect(() => {
    if (!checkIn || !checkOut) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const timer = setTimeout(() => {
      fetch(
        `${API_BASE}/properties/${propertyId}/availability?checkIn=${checkIn}&checkOut=${checkOut}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (!cancelled) {setQuote(data);}
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) {setQuoteLoading(false);}
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [propertyId, checkIn, checkOut]);

  if (isOwner) {
    return null;
  }

  const handleSubmit = async () => {
    if (!user) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    if (!checkIn || !checkOut || !quote?.available) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/bookings`, {
        method: 'POST',
        body: JSON.stringify({ propertyId, checkIn, checkOut, guests: Number(guests) }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit booking request.');
      }
      setSubmitted(true);
      showToast('Booking request sent.', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to submit booking request.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="booking-calendar">
        <Alert type="success">
          Request sent for {checkIn} to {checkOut}. The host will review it, you&rsquo;ll be
          notified once they respond.
        </Alert>
      </div>
    );
  }

  const minNights = defaults?.minNights || 1;
  const maxGuests = defaults?.maxGuests;

  return (
    <div className="booking-calendar">
      <div className="booking-calendar__dates">
        <label>
          Check-in
          <input
            type="date"
            value={checkIn}
            min={defaults?.availabilityStart || undefined}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </label>
        <label>
          Check-out
          <input
            type="date"
            value={checkOut}
            min={checkIn || undefined}
            max={defaults?.availabilityEnd || undefined}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </label>
        <label className="booking-calendar__guests">
          Guests
          <input
            type="number"
            min={1}
            max={maxGuests || undefined}
            value={guests}
            onChange={(e) => setGuests(e.target.value)}
          />
        </label>
      </div>
      <p className="booking-calendar__hint">Minimum stay: {minNights} night(s).</p>

      {quoteLoading && <p className="booking-calendar__hint">Checking availability…</p>}

      {!quoteLoading && quote && (
        <div className="booking-calendar__quote">
          {quote.available ? (
            <>
              <div className="booking-calendar__quote-row">
                <span>
                  {quote.nights} night{quote.nights === 1 ? '' : 's'} x{' '}
                  {formatPrice(quote.pricePerNight, currencyCtx)}
                </span>
                <strong>{formatPrice(quote.totalPrice, currencyCtx)}</strong>
              </div>
              <Badge variant="success">Available</Badge>
            </>
          ) : (
            <Alert type="error">{quote.reasons?.join(' ') || 'Not available for these dates.'}</Alert>
          )}
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!checkIn || !checkOut || !quote?.available || submitting}
      >
        {submitting ? 'Sending…' : user ? 'Request to Book' : 'Log in to request booking'}
      </Button>
    </div>
  );
}
